import React, { useEffect, useState } from 'react';
import api, { formatDate } from '../services/api';
import './VisitaAsistenciaEditor.css';

const asistioToRadio = (v) => {
  if (v === true) return 'si';
  if (v === false) return 'no';
  return 'sin';
};

/**
 * Marcar asistencia del cliente a una cita y comentario opcional.
 * Guarda vía PATCH /visitas/:id/asistencia
 */
const VisitaAsistenciaEditor = ({
  visitaId,
  initialClienteAsistio,
  initialComentarioAsistencia,
  subtitulo,
  readOnly = false,
  compact = false,
  onGuardado,
}) => {
  const [asistio, setAsistio] = useState(() => asistioToRadio(initialClienteAsistio));
  const [comentario, setComentario] = useState(initialComentarioAsistencia || '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setAsistio(asistioToRadio(initialClienteAsistio));
    setComentario(initialComentarioAsistencia || '');
    setError(null);
  }, [visitaId, initialClienteAsistio, initialComentarioAsistencia]);

  const guardar = async () => {
    if (readOnly || !visitaId) return;
    setGuardando(true);
    setError(null);
    try {
      const clienteAsistio = asistio === 'sin' ? null : asistio === 'si';
      const { data } = await api.patch(`/visitas/${visitaId}/asistencia`, {
        clienteAsistio,
        comentarioAsistencia: comentario.trim() ? comentario.trim() : null,
      });
      onGuardado?.(data);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.error || 'No se pudo guardar la asistencia');
    } finally {
      setGuardando(false);
    }
  };

  const name = `cliente-asistio-${visitaId}`;

  return (
    <div className={`visita-asistencia-editor${compact ? ' compact' : ''}`}>
      {subtitulo ? <p className="visita-asistencia-sub">{subtitulo}</p> : null}
      <fieldset
        disabled={readOnly}
        style={{ border: 'none', margin: 0, padding: 0 }}
        aria-label="Asistencia del cliente a la cita"
      >
        <div className="visita-asistencia-opciones">
          <label>
            <input
              type="radio"
              name={name}
              checked={asistio === 'sin'}
              onChange={() => setAsistio('sin')}
            />
            Sin registrar
          </label>
          <label>
            <input
              type="radio"
              name={name}
              checked={asistio === 'si'}
              onChange={() => setAsistio('si')}
            />
            Asistió
          </label>
          <label>
            <input
              type="radio"
              name={name}
              checked={asistio === 'no'}
              onChange={() => setAsistio('no')}
            />
            No asistió
          </label>
        </div>
        <label className="visita-asistencia-comentario-lbl">
          Comentario (opcional)
          <textarea
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            rows={2}
            maxLength={600}
            placeholder="Ej. llegó 15 min tarde, reagendar por teléfono…"
            disabled={readOnly}
          />
        </label>
      </fieldset>
      {!readOnly ? (
        <div className="visita-asistencia-actions">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={guardar}
            disabled={guardando}
          >
            {guardando ? (
              <>
                <i className="fas fa-spinner fa-spin" /> Guardando…
              </>
            ) : (
              <>
                <i className="fas fa-save" /> Guardar asistencia
              </>
            )}
          </button>
        </div>
      ) : null}
      {error ? <p className="visita-asistencia-error">{error}</p> : null}
    </div>
  );
};

export const etiquetaAsistencia = (clienteAsistio) => {
  if (clienteAsistio === true) return { texto: 'Asistió', clase: 'si' };
  if (clienteAsistio === false) return { texto: 'No asistió', clase: 'no' };
  return { texto: 'Sin registrar', clase: 'sin' };
};

/**
 * Asistencia ya guardada (sí/no): mismo aspecto que una nota, solo lectura, con opción de borrar registro.
 */
export function VisitaAsistenciaResumenNota({
  visita,
  lab,
  puedeEliminar,
  showConfirm,
  showError,
  onActualizada,
}) {
  const [eliminando, setEliminando] = useState(false);
  const [expandido, setExpandido] = useState(false);

  const eliminarRegistro = async () => {
    if (!visita?.id || !showConfirm) return;
    const ok = await showConfirm(
      '¿Borrar el registro de asistencia de esta cita? Podrás volver a marcarla más tarde.'
    );
    if (!ok) return;
    setEliminando(true);
    try {
      const { data } = await api.patch(`/visitas/${visita.id}/asistencia`, {
        clienteAsistio: null,
        comentarioAsistencia: null,
      });
      onActualizada?.(data);
    } catch (e) {
      console.error(e);
      showError?.(e.response?.data?.error || 'No se pudo borrar el registro');
    } finally {
      setEliminando(false);
    }
  };

  const fechaStr = formatDate(visita.fecha);
  const comentario = (visita.comentarioAsistencia || '').trim();
  const comentarioPreview =
    comentario.length > 120 ? `${comentario.slice(0, 120).trimEnd()}...` : comentario;

  return (
    <div className="nota-card visita-asistencia-resumen-card" style={{ marginBottom: '0.6rem' }}>
      <div className="nota-card-header">
        <div className="nota-card-author">
          <i className="fas fa-calendar-check" />
          <div>
            <strong>Asistencia a cita</strong>
            <span className="nota-card-rol">{visita.tipo || ''}</span>
          </div>
        </div>
        <div className="nota-card-meta">
          <span className="nota-card-fecha">
            <i className="fas fa-calendar-alt" />
            {fechaStr} — {visita.hora}
          </span>
          <span className={`visita-asistencia-badge ${lab.clase}`} style={{ marginLeft: '6px' }}>
            {lab.texto}
          </span>
          {puedeEliminar ? (
            <button
              type="button"
              className="btn-icon btn-danger"
              onClick={eliminarRegistro}
              disabled={eliminando}
              title="Borrar registro de asistencia"
            >
              <i className={`fas ${eliminando ? 'fa-spinner fa-spin' : 'fa-trash'}`} />
            </button>
          ) : null}
        </div>
      </div>
      <div className="nota-card-body">
        <div className="nota-card-contenido" style={{ whiteSpace: 'pre-wrap' }}>
          <strong>Resultado:</strong> {lab.texto}
          {comentario ? (
            <>
              {'\n\n'}
              <strong>Comentario:</strong>{'\n'}
              {expandido ? comentario : comentarioPreview}
            </>
          ) : null}
        </div>
        {comentario ? (
          <button
            type="button"
            className="btn btn-link btn-sm nota-ver-mas"
            onClick={() => setExpandido((prev) => !prev)}
          >
            {expandido ? 'Ver menos' : 'Ver detalle'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default VisitaAsistenciaEditor;
